import { useEffect, useState } from 'react';
import { Card, Wizard } from '@/components/ui';
import type { WizardStep } from '@/components/ui/Wizard';
import { FormField } from '@/components/ui/FormField';
import { TextInput } from '@/components/ui/TextInput';
import { Icon } from '@/components/ui/Icon';
import { toast } from '@/components/ui/Toast';
import { adminApi, type CampaignReadiness } from '@/shared/api/adminApi';
import { LeadImportInput, type ParsedLead } from '../leads/LeadImportInput';

/**
 * אשף הקמת קמפיין.
 *
 * 🔴 מה שהוא בא לתקן: האשף הקודם היה 11 שלבים שרובם טקסט בלבד —
 * "אפשר להעלות לידים אחרי יצירת הקמפיין", "הקצה נציגים במסך הניהול".
 * הוא יצר קמפיין בלי לידים, בלי נציגים ובלי מספר יוצא, והחסם התגלה רק
 * כשנציג לחץ "חייג" וקיבל שגיאה. מנהל שסיים אשף שלם היה בטוח שסיים.
 *
 * 🔑 העיקרון: **האשף לא ממציא נקודות קצה.** הוא מחבר את מה שכבר קיים
 * ובדוק — יצירה, שיוך נציגים, בנק מספרים, ייבוא לידים, ובדיקת מוכנות —
 * ומריץ אותם לפי הסדר. כך אין מסלול יצירה שני שמתפצל בשקט מהראשון.
 *
 * הזרימה:
 *   1. POST /admin/campaigns              → קמפיין במצב draft
 *   2. PUT  campaigns/{id}/agents         → מי מחייג
 *   3. PUT  campaigns/{id}/dids           → מאיזה מספר
 *   4. POST /admin/leads/import           → למי מחייגים (+ דוח ייבוא אמיתי)
 *   5. GET  campaigns/{id}/readiness      → מה עוד חוסם, לפי השרת
 *
 * שלב 5 הוא הלב: מסך הסיום מציג את החסמים ש**השרת** מדווח עליהם, ולא
 * ניחוש של הדפדפן. קמפיין שנוצר חסום אומר זאת במפורש, עם הסיבה.
 *
 * מה שבמכוון *לא* כאן: 15 שדות ההגדרות המתקדמות (סולם ניסיונות, פעולות
 * מהירות, רוטציית מספרים). הם חיים בלשונית "הגדרות מתקדמות" בכרטיס
 * הקמפיין. אשף שקובר אותם הוא אשף שלא מסיימים.
 */

interface AgentRow { id: number; name: string; extension?: string | null }
interface DidRow { id: number; number: string; burned?: boolean }

const PACE = [
  {
    mode: 'preview',
    title: 'הנציג מחליט',
    detail: 'הנציג רואה את פרטי הלקוח ולוחץ "חייג" בעצמו. הקצב האיטי ביותר — מתאים לשיחות מורכבות או ללידים יקרים.',
  },
  {
    mode: 'progressive',
    title: 'שיחה אחת לכל נציג',
    detail: 'המערכת מחייגת ללקוח הבא ברגע שנציג מתפנה. קצב אחיד בלי זמן מת, והלקוח תמיד מגיע לנציג זמין.',
  },
  {
    mode: 'predictive',
    title: 'כמה שיחות במקביל',
    detail: 'המערכת מחייגת לכמה לקוחות בו-זמנית לכל נציג פנוי. הקצב המהיר ביותר — אבל שיחה עלולה להינטש אם אף נציג לא התפנה.',
  },
] as const;

export function CampaignSetupWizard({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const [agents, setAgents] = useState<AgentRow[]>([]);
  const [dids, setDids] = useState<DidRow[]>([]);
  const [leads, setLeads] = useState<ParsedLead[]>([]);
  const [saving, setSaving] = useState(false);
  // תוצאת ההקמה — מוצגת אחרי היצירה, עם החסמים האמיתיים מהשרת.
  const [result, setResult] = useState<{ id: number; name: string; readiness: CampaignReadiness | null } | null>(null);

  useEffect(() => {
    void adminApi.agents({ per_page: 200 })
      .then((p) => setAgents(((p?.data ?? []) as AgentRow[]).filter((a) => a?.id)))
      .catch(() => {});
    // מספר שרוף אינו מספר זמין — אותו תנאי כמו ב-CampaignCallerIdResolver.
    void adminApi.dids()
      .then((p) => setDids(((p?.data ?? []) as DidRow[]).filter((d) => d?.id && !d.burned)))
      .catch(() => {});
  }, []);

  const steps: WizardStep[] = [
    {
      id: 'who',
      title: 'למי מחייגים',
      validate: (d) => {
        if (!String(d.name ?? '').trim()) return 'יש לתת שם לקמפיין';
        return true;
      },
      render: (ctx) => (
        <div className="stack">
          <FormField label="שם הקמפיין" required>
            <TextInput
              value={(ctx.data.name as string) ?? ''}
              onChange={(e) => ctx.setData({ name: e.target.value })}
              placeholder="לדוגמה: לקוחות שהתעניינו בספטמבר"
            />
          </FormField>

          <FormField
            label="רשימת לידים"
            hint="טלפון ואז שם, שורה לכל ליד. אפשר גם לדלג ולהעלות אחר כך — אבל קמפיין בלי לידים לא יחייג."
          >
            <LeadImportInput onParsed={setLeads} />
          </FormField>
          {leads.length > 0 && (
            <p style={{ margin: 0, color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>
              {leads.length} לידים זוהו. מספרים שגויים וכפילויות יסוננו בייבוא ויוצגו בסיכום.
            </p>
          )}
        </div>
      ),
    },
    {
      id: 'how',
      title: 'מי מחייג ואיך',
      render: (ctx) => {
        const picked = (ctx.data.agent_ids as number[]) ?? [];
        const pickedDids = (ctx.data.did_ids as number[]) ?? [];
        const toggle = (list: number[], id: number) =>
          list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

        return (
          <div className="stack">
            <FormField label="נציגים" hint="בלי נציג משויך אף אחד לא יוכל לחייג בקמפיין.">
              {agents.length === 0 ? (
                <p style={{ margin: 0, color: 'var(--muted)' }}>לא נמצאו נציגים. אפשר לשייך אותם אחר כך במסך הקמפיין.</p>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {agents.map((a) => {
                    const on = picked.includes(a.id);
                    return (
                      <button
                        key={a.id}
                        type="button"
                        aria-pressed={on}
                        className={`btn btn-sm${on ? ' primary' : ''}`}
                        onClick={() => ctx.setData({ agent_ids: toggle(picked, a.id) })}
                      >
                        {on && <Icon name="ok" size={13} />} {a.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </FormField>

            <FormField label="מספר יוצא" hint="המספר שהלקוח יראה. חייב להיות מספר של העסק — אחרת כל חיוג ייכשל.">
              {dids.length === 0 ? (
                <p style={{ margin: 0, color: 'var(--muted)' }}>לא נמצאו מספרים פנויים. אפשר לשייך מספר אחר כך במסך הקמפיין.</p>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {dids.map((d) => {
                    const on = pickedDids.includes(d.id);
                    return (
                      <button
                        key={d.id}
                        type="button"
                        aria-pressed={on}
                        className={`btn btn-sm${on ? ' primary' : ''}`}
                        onClick={() => ctx.setData({ did_ids: toggle(pickedDids, d.id) })}
                        dir="ltr"
                      >
                        {on && <Icon name="ok" size={13} />} {d.number}
                      </button>
                    );
                  })}
                </div>
              )}
            </FormField>
          </div>
        );
      },
    },
    {
      id: 'pace',
      title: 'קצב החיוג',
      render: (ctx) => {
        const mode = (ctx.data.dialer_mode as string) ?? 'progressive';
        return (
          <div className="stack">
            {PACE.map((p) => {
              const on = mode === p.mode;
              return (
                <button
                  key={p.mode}
                  type="button"
                  aria-pressed={on}
                  onClick={() => ctx.setData({ dialer_mode: p.mode })}
                  style={{
                    textAlign: 'start',
                    padding: 12,
                    borderRadius: 'var(--r-2)',
                    border: `1px solid ${on ? 'var(--brand)' : 'var(--line)'}`,
                    background: on ? 'var(--surface-2)' : 'var(--surface)',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700 }}>
                    {on && <Icon name="ok" size={14} />}
                    {p.title}
                  </div>
                  <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)', lineHeight: 1.6, marginTop: 4 }}>
                    {p.detail}
                  </div>
                </button>
              );
            })}
            <p style={{ margin: 0, color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>
              שאר ההגדרות (ניסיונות, סולם חיוג, קודי סיום) מוגדרות בכרטיס הקמפיין אחרי ההקמה.
            </p>
          </div>
        );
      },
    },
    {
      id: 'review',
      title: 'סיום',
      render: (ctx) => {
        // אחרי ההקמה — מציגים את מה שהשרת אומר, לא ניחוש.
        if (result) {
          const r = result.readiness;
          return (
            <div className="stack">
              <Card>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700 }}>
                  <Icon name="ok" size={16} /> הקמפיין "{result.name}" נוצר כטיוטה
                </div>
              </Card>
              {r && !r.ready && (
                <Card>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>לפני שאפשר לחייג:</div>
                  <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                    {r.blockers.map((b) => (
                      <li key={b.code} style={{ marginBottom: 4 }}>
                        <strong>{b.label}</strong>
                        <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>{b.detail}</div>
                      </li>
                    ))}
                  </ul>
                </Card>
              )}
              {r?.ready && (
                <Card>
                  <div style={{ color: 'var(--ok, green)' }}>
                    הקמפיין מוכן לחיוג — {r.counts.dialable_leads} לידים, {r.counts.agents} נציגים, {r.counts.dids} מספרים.
                    הפעל אותו ממסך הקמפיינים.
                  </div>
                </Card>
              )}
            </div>
          );
        }

        const picked = ((ctx.data.agent_ids as number[]) ?? []).length;
        const pickedDids = ((ctx.data.did_ids as number[]) ?? []).length;
        const mode = (ctx.data.dialer_mode as string) ?? 'progressive';
        const warn = [
          leads.length === 0 && 'אין לידים — לא יהיה למי לחייג',
          picked === 0 && 'לא נבחרו נציגים — אף אחד לא יוכל לחייג',
          pickedDids === 0 && 'לא נבחר מספר יוצא — כל חיוג ייכשל',
        ].filter(Boolean) as string[];

        return (
          <div className="stack">
            <Card>
              <div style={{ display: 'grid', gap: 8 }}>
                <Row label="שם" value={String(ctx.data.name ?? '—')} />
                <Row label="לידים" value={leads.length ? String(leads.length) : '—'} />
                <Row label="נציגים" value={picked ? String(picked) : '—'} />
                <Row label="מספרים" value={pickedDids ? String(pickedDids) : '—'} />
                <Row label="קצב" value={PACE.find((p) => p.mode === mode)?.title ?? mode} />
              </div>
            </Card>
            {warn.length > 0 && (
              <Card>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>אפשר להמשיך, אבל:</div>
                <ul style={{ margin: 0, paddingInlineStart: 18, color: 'var(--muted)' }}>
                  {warn.map((w) => <li key={w}>{w}</li>)}
                </ul>
                <div style={{ marginTop: 6, fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>
                  הקמפיין ייווצר כטיוטה וניתן להשלים הכל במסך הקמפיין.
                </div>
              </Card>
            )}
          </div>
        );
      },
    },
  ];

  /**
   * ההקמה עצמה. כל שלב הוא נקודת קצה קיימת ובדוקה.
   *
   * 🔑 הקמפיין נוצר ראשון ובמצב draft: בלי מזהה אי אפשר לשייך נציגים,
   * מספרים או לידים. כישלון בשלב מאוחר אינו מוחק את הקמפיין — עדיף
   * קמפיין חלקי שהמנהל משלים, מאשר עבודה שנמחקה בלי להודיע.
   */
  async function finish(data: Record<string, unknown>) {
    if (saving || result) { onDone(); return; }
    setSaving(true);
    try {
      const { campaign } = await adminApi.createCampaign({
        name: data.name,
        dialer_mode: (data.dialer_mode as string) ?? 'progressive',
      });
      const id = campaign?.id as number;
      if (!id) throw new Error('no id');

      const agentIds = (data.agent_ids as number[]) ?? [];
      const didIds = (data.did_ids as number[]) ?? [];

      // כל שלב נכשל בנפרד ואינו מפיל את השאר: מספר שנדחה לא אמור
      // לאבד את הלידים שכבר יובאו.
      if (agentIds.length) {
        await adminApi.syncCampaignAgents(id, agentIds).catch(() => toast('שיוך הנציגים נכשל — אפשר לשייך במסך הקמפיין', 'warn'));
      }
      if (didIds.length) {
        await adminApi.saveCampaignDids(id, didIds).catch(() => toast('שיוך המספרים נכשל — אפשר לשייך במסך הקמפיין', 'warn'));
      }
      if (leads.length) {
        const rep = await adminApi.importLeads(id, leads).catch(() => null);
        if (rep && typeof rep.imported === 'number') {
          const dup = rep.skipped_duplicates ?? 0;
          toast(`יובאו ${rep.imported} לידים${dup ? ` · ${dup} כפילויות דולגו` : ''}`, 'ok');
        }
      }

      const readiness = await adminApi.campaignReadiness(id).catch(() => null);
      setResult({ id, name: String(data.name ?? ''), readiness });
    } catch {
      toast('יצירת הקמפיין נכשלה', 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Wizard
      steps={steps}
      initialData={{ dialer_mode: 'progressive', agent_ids: [], did_ids: [] }}
      onFinish={(d) => { void finish(d); }}
      onCancel={onCancel}
    />
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 'var(--t-sm)' }}>
      <span style={{ color: 'var(--muted)' }}>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
